import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/content';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiAlertCircle, FiCheckCircle, FiArrowRight, FiUserPlus, FiLogIn, FiLoader } = FiIcons;

const Auth = ({ initialIsLogin = true, embedded = false }) => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const t = translations[lang].auth;
  
  const [isLogin, setIsLogin] = useState(initialIsLogin);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    setIsLogin(initialIsLogin);
  }, [initialIsLogin]);

  useEffect(() => {
    if (embedded) return; // Don't redirect if embedded in a step

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) navigate('/steps');
    };
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        navigate('/steps');
      }
    });

    return () => subscription?.unsubscribe();
  }, [navigate, embedded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isLogin) {
        const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
        if (loginError) throw loginError;
      } else {
        // 1. Check if user already exists via RPC for better UX
        const { data: exists, error: checkError } = await supabase.rpc('check_user_exists', { email_input: email });
        
        if (checkError) {
          console.error('Error checking user existence:', checkError);
        } else if (exists) {
          throw new Error(t.userExistsError || 'An account with this email already exists. Please log in instead.');
        }

        // 2. Perform Sign Up
        const { error: signUpError } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            emailRedirectTo: window.location.origin + '/#/steps'
          }
        });
        
        if (signUpError) {
          // Fallback check in case RPC missed it
          if (signUpError.message.toLowerCase().includes('already registered')) {
            throw new Error(t.userExistsError || 'An account with this email already exists. Please log in instead.');
          }
          throw signUpError;
        }

        setSuccess(lang === 'de' ? 'Registrierung erfolgreich! Willkommen an Bord.' : 'Signup successful! Welcome aboard.');
        
        // If embedded, the parent component will detect the auth change
        // If not embedded, the useEffect will redirect to /steps or /welcome
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className={`w-full ${embedded ? '' : 'max-w-md bg-white rounded-[2.5rem] shadow-2xl shadow-blue-900/5 border border-gray-100 overflow-hidden'}`}>
      {!embedded && (
        <div className="p-8 pb-4 flex flex-col items-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-20 h-20 rounded-3xl bg-blue-50 flex items-center justify-center mb-6 shadow-inner"
          >
            <SafeIcon icon={isLogin ? FiLogIn : FiUserPlus} className="text-3xl text-blue-600" />
          </motion.div>
          <h1 className="text-2xl md:text-3xl font-black text-gray-900 text-center uppercase tracking-tight">
            {isLogin ? t.loginTitle : t.title}
          </h1>
          <p className="text-gray-500 text-center mt-2 font-medium">
            {isLogin ? t.loginSubtitle : t.subtitle}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className={`${embedded ? 'p-0' : 'p-8'} space-y-5`}>
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-50 border border-red-100 text-red-700 px-5 py-4 rounded-2xl text-sm flex items-start space-x-3 font-semibold"
            >
              <SafeIcon icon={FiAlertCircle} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p>{error}</p>
                {error.includes(t.userExistsError || 'already exists') && (
                  <button 
                    type="button"
                    onClick={() => { setIsLogin(true); setError(null); }}
                    className="mt-2 text-blue-600 hover:underline flex items-center space-x-1"
                  >
                    <span>{lang === 'de' ? 'Jetzt anmelden' : 'Log in now'}</span>
                    <SafeIcon icon={FiArrowRight} />
                  </button>
                )}
              </div>
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-green-50 border border-green-100 text-green-700 px-5 py-4 rounded-2xl text-sm flex items-center space-x-3 font-semibold"
            >
              <SafeIcon icon={FiCheckCircle} className="flex-shrink-0" />
              <span>{success}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">{t.email}</label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-500 transition-colors">
              <SafeIcon icon={FiMail} />
            </div>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-11 pr-4 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-blue-500 focus:bg-white outline-none transition-all font-medium text-gray-900"
              placeholder="name@example.com"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">{t.password}</label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-500 transition-colors">
              <SafeIcon icon={FiLock} />
            </div>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-11 pr-4 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-blue-500 focus:bg-white outline-none transition-all font-medium text-gray-900"
              placeholder="••••••••"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white font-black py-5 rounded-2xl shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all active:scale-[0.98] disabled:opacity-70 flex items-center justify-center space-x-3 uppercase tracking-widest text-sm"
        >
          {loading ? (
            <SafeIcon icon={FiLoader} className="animate-spin text-xl" />
          ) : (
            <>
              <span>{isLogin ? (lang === 'de' ? 'Anmelden' : 'Sign In') : t.submit}</span>
              <SafeIcon icon={FiArrowRight} />
            </>
          )}
        </button>

        <div className="pt-4 text-center">
          <button
            type="button"
            onClick={() => { setIsLogin(!isLogin); setError(null); setSuccess(null); }}
            className="text-gray-500 font-bold hover:text-blue-600 text-sm transition-colors flex items-center justify-center mx-auto space-x-2"
          >
            <span>{isLogin ? t.noAccount : t.alreadyHaveAccount}</span>
          </button>
        </div>
      </form>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 flex items-center justify-center p-4 py-20">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        {content}
      </motion.div>
    </div>
  );
};

export default Auth;